import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useState } from 'react'
import {
  GeckoKestrelModel,
  KestrelInspectionType,
  KVAnnotation,
  KVCoordinate,
  RolodexEntryType,
} from 'types'

export const useHistoricalAnnotations = (kestrelModel?: GeckoKestrelModel) => {
  const [historicalAnnotations, setHistoricalAnnotations] = useState<
    KVAnnotation[]
  >([])
  const { entries: historicalModels, isLoading: historicalModelsLoaded } =
    useRolodexEntries(
      !kestrelModel?.data.underlyingAsset || !kestrelModel?.data.scanDate
        ? []
        : {
            type: RolodexEntryType.KESTREL_MODEL,
            filters: [
              {
                key: 'underlyingAsset',
                op: '=',
                value: kestrelModel.data.underlyingAsset,
                cast: 'string',
              },
              {
                key: 'scanDate',
                op: '<=',
                value: kestrelModel.data.scanDate,
                cast: 'number',
              },
            ],
            order_by: [{ key: 'scanDate', descending: true }],
          }
    )

  useEffect(() => {
    if (
      kestrelModel?.data?.underlyingAsset &&
      kestrelModel?.data.inspection_type === KestrelInspectionType.ROOFTOP
    ) {
      const toOrigin = kestrelModel.data.origin
      const toMeasurementScale = kestrelModel.data.measurementScale

      if (toOrigin) {
        const models = historicalModels.filter(
          (model) => model.id !== kestrelModel.id
        ) as GeckoKestrelModel[]

        const updatedAnnotations = models
          .map((model) => {
            if (model.data.origin) {
              const fromOrigin = model.data.origin
              const fromMeasurementScale = model.data.measurementScale
              const annotation = model.data.annotations

              const updatedAnnotation = annotation.map((a) => {
                const geometry = a?.geometry
                a.historical = true
                a.tags.historical = { derivedTag: true, value: 'true' }

                if (geometry?.kind === 'point') {
                  if (geometry.position) {
                    geometry.position = KVCoordinate.betweenWorlds(
                      fromOrigin,
                      toOrigin,
                      geometry.position,
                      fromMeasurementScale,
                      toMeasurementScale
                    )

                    a.centeredPosition = geometry.position
                    a.centeredNormal = a.centeredNormal
                      ? KVCoordinate.betweenWorlds(
                          fromOrigin,
                          toOrigin,
                          a.centeredNormal,
                          fromMeasurementScale,
                          toMeasurementScale
                        )
                      : undefined
                  }
                }

                if (geometry?.kind === 'polygon') {
                  geometry.points = geometry.points.map((p) =>
                    KVCoordinate.betweenWorlds(
                      fromOrigin,
                      toOrigin,
                      p,
                      fromMeasurementScale,
                      toMeasurementScale
                    )
                  )

                  let x = 0,
                    y = 0,
                    z = 0
                  for (const p of geometry.points) {
                    x += p.x
                    y += p.y
                    z += p.z
                  }

                  a.centeredPosition = {
                    x: x / geometry.points.length,
                    y: y / geometry.points.length,
                    z: z / geometry.points.length,
                  }
                  a.centeredNormal = a.centeredNormal
                    ? KVCoordinate.betweenWorlds(
                        fromOrigin,
                        toOrigin,
                        a.centeredNormal,
                        fromMeasurementScale,
                        toMeasurementScale
                      )
                    : undefined
                }

                a.geometry = geometry
                return a
              })

              return updatedAnnotation
            }
            return []
          })
          .flat()

        setHistoricalAnnotations(updatedAnnotations)
      }
    }
  }, [
    historicalModels,
    historicalModelsLoaded,
    kestrelModel,
    setHistoricalAnnotations,
  ])

  return {
    fetchedHistoricalAnnotations: historicalAnnotations,
  }
}
